import { Component, Inject, Input, OnDestroy, ViewChild, DOCUMENT, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { Router } from '@angular/router';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventApi } from '@fullcalendar/core';
import {
  EventStore,
  EventMutation,
  applyMutationToEventStore,
  EventUiHash,
  buildEventApis,
} from '@fullcalendar/core/internal';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { committee_calendar_dates } from './graphql/committeeCalendarDates';
import { committee_calendar_date_hover } from './graphql/committeeCalendarDateHover';
import { committee_calendar_ready_dates } from './graphql/committeeCalendarReadyDates';
import { EnvironmentService } from '../environment/environment.service';
import { EventManagerService } from './event-manager.service';
import { blocked_committee_dates } from './graphql/blockedCommitteeDates';
import { add_blocked_committee_date } from './graphql/addBlockedCommitteeDate';
import { delete_blocked_committee_date } from './graphql/deleteBlockedCommitteeDate';
import { committee_calendar_fund_close_date_hover } from './graphql/committeeCalendarFundCloseDateHover';

const GET_COMMITTEE_DATES = committee_calendar_dates;
const GET_COMMITTEE_DATE_HOVER = committee_calendar_date_hover;
const GET_FUND_CLOSE_DATE_HOVER = committee_calendar_fund_close_date_hover;
const GET_COMMITTEE_READY_DATES = committee_calendar_ready_dates;
const GET_BLOCKED_COMMITTEE_DATES = blocked_committee_dates;
const ADD_BLOCKED_COMMITTEE_DATE = add_blocked_committee_date;
const DELETE_BLOCKED_COMMITTEE_DATE = delete_blocked_committee_date;

import moment from 'moment';

import HashMap from '../types/hash-map';
import {CurrentUserService} from "../services/current-user.service";
import Project from "../interfaces/project";
import Participant from "../interfaces/participant";


type StatusMap = {
  id: string;
  index: number;
};

@Component({
    selector: 'app-committee-calendar',
    templateUrl: './committee-calendar.component.html',
    styleUrls: ['./committee-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommitteeCalendarComponent implements OnDestroy {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  private querySubscription: Subscription = new Subscription();
  private readyDateSubscription: Subscription = new Subscription();
  private blockedDatesSubscription: Subscription = new Subscription();
  private statusMap: HashMap<StatusMap> = {};
  constructor(
    private apollo: Apollo,
    private env: EnvironmentService,
    public eventManagerService: EventManagerService,
    private router: Router,
    private currentUser: CurrentUserService,
    @Inject(DOCUMENT) public document: Document,
    private changeDetector: ChangeDetectorRef
  ) {}

  results: any;
  pipelineMilestoneProjectIDs: string[] = [];
  screeningProjectIDs: string[] = [];
  currentUserId: string | undefined;
  blockedResults: any;
  blockDatesActivate: boolean = false;
  blockedWaiting: boolean = false;
  start: string = '';
  end: string = '';
  _calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    headerToolbar: {
      start: '',
      center: 'title',
      end: 'today prev,next',
    },
    aspectRatio: 1.8,
    height: 'auto',
    displayEventTime: false,
    eventDisplay: 'block',
    expandRows: true,
    datesSet: this.monthRender.bind(this),
    eventMouseEnter: this.hoverEvent.bind(this),
    eventContent: this.addIcons.bind(this),
    eventClick: this.eventClick.bind(this),
    // for some reason dateClick causes issues with karma
    // @ts-ignore
    dateClick: this.dayClick.bind(this),
    plugins: [dayGridPlugin, interactionPlugin],
  };
  _events: any[] = [];
  events = new BehaviorSubject(this._events);
  events$ = this.events.asObservable();

  calendarOptions = new BehaviorSubject(this._calendarOptions);
  calendarOptions$ = this.calendarOptions.asObservable();

  _dates: any = {};
  dates = new BehaviorSubject<any>(this._dates);
  dates$ = this.dates.asObservable();
  dateSub: Subscription;

  _readyDates: any = {};
  readyDates = new BehaviorSubject<any>(this._readyDates);
  readyDates$ = this.readyDates.asObservable();

  dateChangeSub: Subscription;
  renderCalendar: boolean = true;
  eventsChangeSub: Subscription;

  ngOnInit(): void
  {
    this.currentUser.getUser().subscribe((result) => this.currentUserId = result?.person?.id)
    let readyDateSubscription: Subscription;
    this.dateSub = this.dates$.subscribe((dates) => {
      this.mapStatuses(dates);
      this.getProjectIds(dates);
      if (
        this.pipelineMilestoneProjectIDs.length > 0 ||
        this.screeningProjectIDs.length > 0
      ) {
        readyDateSubscription = this.apollo
          .query<any>({
            query: GET_COMMITTEE_READY_DATES,
            variables: {
              screeningProjects: this.screeningProjectIDs,
              projects: this.pipelineMilestoneProjectIDs,
            },
          })
          .subscribe(({ data }) => {

            this._readyDates = data;
            this.readyDates.next(this._readyDates);
            readyDateSubscription.unsubscribe();
          });
      }
    });
    this.dateChangeSub = combineLatest({readyDates: this.readyDates$, dates: this.dates$}).subscribe(({dates, readyDates})=>{
      this._events = [...this.formatFinalResults(
        dates,
        readyDates
      )];
      this.events.next(this._events)
    })
  }
  monthRender(arg: any): void {
    let start = arg.start.toISOString().slice(0, 10);
    let end = arg.end.toISOString().slice(0, 10);
    this.getCalendarDates(start, end);
    this.getBlockedCommitteeDates(start, end);
  }

  getCalendarDates(start: string, end: string): void {
    let querySubscription: Subscription;
    this.start = start;
    this.end = end;
    querySubscription = this.apollo
      .query<any>({
        query: GET_COMMITTEE_DATES,
        variables: {
          start: start,
          end: end,
          pipestart: start,
          pipeend: end,
        },
      })
      .subscribe(({ data }) => {
        if (data) {
          let first = <object>data;
          this._dates = data;
          this.dates.next(this._dates);
          querySubscription.unsubscribe();
        }});
  }
  mapStatuses(data: {
    statusSequenceDetails: {
      index: number;
      status: { id: string; name: string };
    }[];
  }) {
    if(data.statusSequenceDetails) {
      for (let statusSequenceDetail of data.statusSequenceDetails) {
        this.statusMap[statusSequenceDetail.status.name] = {
          id: statusSequenceDetail.status.id,
          index: statusSequenceDetail.index,
        };
      }
    }
  }
  getStatusIndex(status_id: string) {
    let statusName = '';
    for (let key of Object.keys(this.statusMap)) {
      if (this.statusMap[key].id === status_id) {
        statusName = key;
        break;
      }
    }
    return this.statusMap[statusName]?.index ?? -1;
  }
  //Adds Project IDs to arrays to query the ready dates
  getProjectIds(data: any): void {
    if (data.pipelineMilestoneDates && data.pipelineMilestoneDates.length > 0) {
      for (let i = 0; i < data.pipelineMilestoneDates.length; i++) {
        for (
          let j = 0;
          j < data.pipelineMilestoneDates[i]['pipelineProjectDates'].length;
          j++
        ) {
          if (
            data.pipelineMilestoneDates[i]['milestone']['name'] ===
              'Screening Committee' &&
            data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
              'target'
            ] &&
            !this.screeningProjectIDs.includes(
              data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                'pipelineProject'
              ]['project']['id']
            )
          ) {
            this.screeningProjectIDs.push(
              data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                'pipelineProject'
              ]['project']['id']
            );
          }

          if (
            !this.pipelineMilestoneProjectIDs.includes(
              data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                'pipelineProject'
              ]['project']['id']
            )
          ) {
            this.pipelineMilestoneProjectIDs.push(
              data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                'pipelineProject'
              ]['project']['id']
            );
          }
        }
      }
    }
  }

  isUserDeal(project: Project){
    if(!this.currentUserId || !project || !project.participants){
      return false;
    }
    return project.participants.some((participant: Participant) => participant.person?.id === this.currentUserId)
  }

  //Adds the ready dates to first results and formats for calendar render
  formatFinalResults(data: any, readydates: any): any {
    let format: any[] = [];
    if (data.pipelineMilestoneDates && data.pipelineMilestoneDates.length > 0) {
      for (let i = 0; i < data.pipelineMilestoneDates.length; i++) {
        for (
          let j = 0;
          j < data.pipelineMilestoneDates[i]['pipelineProjectDates'].length;
          j++
        ) {
          let date =
            data.pipelineMilestoneDates[i]['pipelineProjectDates'][j]['date'];
          let project =
            data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
              'pipelineProject'
            ]['project'];
          let screeningdate = moment.utc(date);
          let targetdate = undefined;
          let targetisuw = undefined;
          let targetIsGTE14days = undefined;
          let milestone = data.pipelineMilestoneDates[i]['milestone']['name'];
          let eventStyling = this.eventManagerService.generateEventData(
            milestone,
            date
          );
          if (milestone === 'Closed' && project.projectStatuses) {
            let status = project.projectStatuses.find((projectStatus: any) => {
              let index = this.getStatusIndex(projectStatus.status_id);
              return index > -1 && index < this.statusMap['Signed LOI'].index;
            });
            if (status) {
              eventStyling = this.eventManagerService.generateEventData(
                'Closed (Pre-LOI)',
                date
              );
            }
          }
          //Grab latest ready date for Screening
          if (milestone === 'Screening Committee') {
            if (readydates && readydates.projectDates?.length > 0) {
              for (let k = 0; k < readydates.projectDates.length; k++) {
                let rdate = moment.utc(readydates.projectDates[k].date);
                if (
                  readydates.projectDates[k].project.id === project['id'] &&
                  data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                    'target'
                  ] &&
                  rdate > screeningdate &&
                  (targetdate === undefined || rdate > targetdate)
                ) {
                  targetdate = moment.utc(readydates.projectDates[k].date);
                  targetisuw =
                    readydates.projectDates[k].dateType.name ===
                    'Underwriting Screening Ready';
                }
              }
            }

            if (targetdate !== undefined) {
              targetIsGTE14days = targetdate.diff(screeningdate, 'days') >= 14;
              targetdate = targetdate.format('M/DD/YYYY');
            }
          }

          if (eventStyling.start) {
            //see if project has a forecast date
            let forecastdate = '';
            if (readydates && readydates.projectMilestoneDates?.length > 0) {
              for (
                let k = 0;
                k < readydates.projectMilestoneDates.length;
                k++
              ) {
                if (
                  data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                    'pipelineProject'
                  ]['project']['id'] ===
                    readydates.projectMilestoneDates[k]['project']['id'] &&
                  data.pipelineMilestoneDates[i]['id'] ===
                    readydates.projectMilestoneDates[k]['pipelineMilestone'][
                      'id'
                    ]
                ) {
                  forecastdate = moment
                    .utc(readydates.projectMilestoneDates[k]['date'])
                    .format('M/DD/YYYY');
                  break;
                }
              }
            }

            format.push({
              title:
                data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                  'pipelineProject'
                ]['project']['name'],
              textColor: 'black',
              description: 'Loading...',
              url:
                this.env.nova_url +
                '/deal/' +
                data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                  'pipelineProject'
                ]['project']['id'],
              id: data.pipelineMilestoneDates[i]['pipelineProjectDates'][j][
                'pipelineProject'
              ]['project']['id'] + '.' + eventStyling.groupName ,
              extendedProps: {
                forecast: forecastdate,
                targetdate: targetdate,
                targetisuw: targetisuw,
                targetIsGTE14days: targetIsGTE14days,
                isproject: true,
                isMine: this.isUserDeal(project)
              },
              ...eventStyling,
              display: this.eventManagerService.computeDisplay(eventStyling.groupName, this.isUserDeal(project))
            });
          }
        }
      }
    }

    if (data.projectDates && data.projectDates?.length > 0) {
      for (let i = 0; i < data.projectDates.length; i++) {
        let eventStyle = this.eventManagerService.generateEventData(
          data.projectDates[i]['dateType']['name'],
          data.projectDates[i]['date']
        );
        if (eventStyle.start) {
          let isMine: boolean = this.isUserDeal(data.projectDates[i]['project']);
          format.push({
            title: data.projectDates[i]['project']['name'],
            textColor: 'black',
            description: 'Loading...',
            url:
              this.env.nova_url +
              '/deal/' +
              data.projectDates[i]['project']['id'],
            id: data.projectDates[i]['project']['id'] +'.'+ eventStyle.groupName,
            extendedProps: {
              isproject: true,
              isMine: isMine
            },
            ...eventStyle,
            display: this.eventManagerService.computeDisplay(eventStyle.groupName, isMine)
          });
        }
      }
    }

    if (data.fundClosingDates && data.fundClosingDates?.length > 0) {
      for (let i = 0; i < data.fundClosingDates.length; i++) {
        let eventStyle = this.eventManagerService.generateEventData(
          'Fund Closing',
          data.fundClosingDates[i]['close_date']
        );
        if (eventStyle.start) {
          format.push({
            title: data.fundClosingDates[i]['name'],
            textColor: 'black',
            description: 'Loading...',
            url:
              this.env.nova_url + '/fund/' + data.fundClosingDates[i]['id'],
            id: data.fundClosingDates[i]['id'] +'.'+eventStyle.groupName,
            extendedProps: {
              isproject: false,
              isMine: false
            },
            ...eventStyle,
            display: this.eventManagerService.computeDisplay(eventStyle.groupName, false)
          });
        }
      }
    }

    if (
      data &&
      data.dispositionPipelineDates &&
      data.dispositionPipelineDates.length > 0
    ) {
      for (let i = 0; i < data.dispositionPipelineDates.length; i++) {
        let date = data.dispositionPipelineDates[i];
        let milestoneName = date.pipelineMilestone.milestone.name;
        if (milestoneName === 'Closed') {
          milestoneName = 'Disposition ' + milestoneName;
        }
        let eventStyle = this.eventManagerService.generateEventData(
          milestoneName,
          data.dispositionPipelineDates[i]['date']
        );
        if (eventStyle.start) {
          let isMine:boolean = this.isUserDeal(data.dispositionPipelineDates[i]['disposition']['project'])
          format.push({
            title:
              data.dispositionPipelineDates[i]['disposition']['project'][
                'name'
              ],
            ...eventStyle,
            description: 'loading',
            textColor: 'white',
            url:
              this.env.nova_url +
              '/deal/dispositions/' +
              data.dispositionPipelineDates[i]['disposition']['project']['id'],
            id: data.dispositionPipelineDates[i]['disposition']['project'][
              'id'
            ] + '.' +eventStyle.groupName,
            extendedProps: {
              isproject: true,
              isMine: isMine
            },
            display: this.eventManagerService.computeDisplay(eventStyle.groupName, isMine)
          });
        }
      }
    }

    if(data && data.projectSubstatusDates && data.projectSubstatusDates.length > 0){
      for(let r = 0; r < data.projectSubstatusDates.length; r++){
        let eventStyle = this.eventManagerService.generateEventData(data.projectSubstatusDates[r]['substatus']['name'], data.projectSubstatusDates[r].date)
        if(eventStyle.start){
          let isMine: boolean = this.isUserDeal(data.projectSubstatusDates[r]['projectStatus']['project'])
          format.push({
            title: data.projectSubstatusDates[r]['projectStatus']['project']['name'],
            textColor: 'white',
            description: 'Loading...',
            url:
              this.env.nova_url +
              '/deal/' +
              data.projectSubstatusDates[r]['projectStatus']['project']['id'],
            id: data.projectSubstatusDates[r]['projectStatus']['project']['id'],
            extendedProps: {
              isproject: true,
              isMine: isMine
            },
            ...eventStyle,
            display: this.eventManagerService.computeDisplay(eventStyle.groupName, isMine)
          });
        }
      }
    }
    let filtered = format.filter((form:any) => form.extendedProps.isMine==true)
    return format;
  }

  getBlockedCommitteeDates(start: string, end: string) {
    this.blockedDatesSubscription = this.apollo
      .watchQuery<any>({
        query: GET_BLOCKED_COMMITTEE_DATES,
        variables: {
          start: start,
          end: end,
        },
      })
      .valueChanges.subscribe(({ data }) => {
        this.blockedResults = data;
        if (
          data &&
          data.blockedCommitteeDates &&
          data.blockedCommitteeDates.length > 0
        ) {
          for (let i = 0; i < data.blockedCommitteeDates.length; i++) {
            //grab the selector with the blocked out date and add the class.
            let element = this.document.querySelectorAll(
              '[data-date="' + data.blockedCommitteeDates[i].date + '"]'
            );

            if (element.length > 0) {
              let today = new Date().toISOString().slice(0, 10);
              if (data.blockedCommitteeDates[i].date === today) {
                element[0].classList.add('blocked-out-today');
              } else {
                element[0].classList.add('blocked-out-days');
              }
            }
          }
        }
      });
  }

  hoverEvent(arg: any): void {
    if (arg.event.extendedProps.isproject) {
      this.apollo
        .watchQuery<any>({
          query: GET_COMMITTEE_DATE_HOVER,
          variables: {
            project: arg.event.id.replace(/\..+/g, ''),
            roles: this.decideRolesForHover(arg.event.extendedProps.groupName),
          },
        })
        .valueChanges.subscribe(({ data }) => {
          let event = arg.event;
          let project = null;
          if(data){
            project = data.project;
          }
          let description = `<span class="tooltip-header">${event.extendedProps.groupName}</span>`;
          if (
            event.extendedProps.hasOwnProperty('forecast') &&
            event.extendedProps.forecast !== ''
          ) {
            description += `<hr><div class="date-paragraph"><i class="material-icons">calendar_month</i> <span class="date-header">Milestone Dates</span><br>`;
            description += `<i class="material-icons tooltip-checkmark">check_circle_icon</i> <span class="date-header">Forecast Approval:</span> ${event.extendedProps.forecast}</div>`;
          }
          if (
            event.extendedProps.hasOwnProperty('targetdate') &&
            event.extendedProps.targetdate !== undefined
          ) {
            if (event.extendedProps.targetIsGTE14days) {
              description +=
                `<hr><div class="date-paragraph"><i class="material-icons tooltip-danger">warning</i> <span class="date-header">` +
                (event.extendedProps.targetisuw
                  ? `Underwriting`
                  : `Construction`) +
                ` Screening Ready:</span> ${event.extendedProps.targetdate}</div>`;
            } else {
              description +=
                `<hr><div class="date-paragraph"><i class="material-icons tooltip-warning">warning</i> <span class="date-header">` +
                (event.extendedProps.targetisuw
                  ? `Underwriting`
                  : `Construction`) +
                ` Screening Ready:</span> ${event.extendedProps.targetdate}</div>`;
            }
          }
          description += `<hr><div class="date-paragraph"><span class="date-header">Repeat Deal:</span> ${project?.is_repeat ? "Yes" : "No"}</div>`;
          description += `<hr><ul>`;
          let roles: { [key: string]: string[] } = {};
          for (let i = 0; i < project?.participants.length; i++) {
            let participant = project.participants[i];
            let role = participant.role.name;
            if (!roles.hasOwnProperty(role)) {
              roles[role] = [];
            }

            roles[role].push(
              participant.person?.name || participant.organization?.name || (participant.sponsor?.nickname + (participant.sponsor?.is_repeat ? " (Repeat)" : ""))
            );
          }
          for (let i = 0; i < project?.projectCredits.length; i++) {
            let credit = project.projectCredits[i];
            for (let j = 0; j < credit.projectCreditOutlets.length; j++) {
              let outlet = credit.projectCreditOutlets[j];
              let role = outlet.fund ? 'Fund' : 'Certificated Outlet:';
              if (!roles.hasOwnProperty(role)) {
                roles[role] = [];
              }
              roles[role].push(
                outlet.fund?.name || outlet.certificatedOutlet?.name
              );
            }
          }
          Object.keys(roles).map((key: string) => {
            description += `<li><span class="participant-bold">${key}:</span><span> ${roles[
              key
            ].join(', ')}</span></li>`;
          });
          description += `</ul>`;
          this.mutateEvent(event, { extendedProps: { description } });
        });
    } else {
      this.apollo
        .watchQuery<any>({
          query: GET_FUND_CLOSE_DATE_HOVER,
          variables: {
            fund: arg.event.id.replace(/\..+/g, ''),
            roles: [
              'Syndications Account Manager',
              'Senior Syndications Analyst',
              'Syndications Analyst',
            ],
          },
        })
        .valueChanges.subscribe(({ data }) => {
          let event = arg.event;
          let project = data.fund;
          let description = `<span class="tooltip-header">${project.ownedEntities[0].name}</span>`;
          description += `<hr><ul>`;
          let roles: { [key: string]: string[] } = {};
          for (let i = 0; i < project.participants.length; i++) {
            let participant = project.participants[i];
            let role = participant.role.name;
            if (!roles.hasOwnProperty(role)) {
              roles[role] = [];
            }
            roles[role].push(
              participant.person?.name || participant.organization.name
            );
          }

          Object.keys(roles).map((key: string) => {
            description += `<li><span class="participant-bold">${key}:</span><span> ${roles[
              key
            ].join(', ')}</span></li>`;
          });
          description += `</ul>`;
          this.mutateEvent(event, { extendedProps: { description } });
        });
    }
  }

  decideRolesForHover(key: string) {
    switch (key) {
      case 'Placement Committee Reviewed':
        return ['Sponsor', 'Co-Sponsor', 'Originator'];
      default:
        return ['Sponsor', 'Co-Sponsor', 'Account Manager'];
    }
  }

  //Alters the event's HTML to add icons
  addIcons(arg: any) {
    let customhtml = `<div class="fc-event-main-frame">`;
    customhtml += `<div class="fc-event-title-container"><div class="fc-event-title fc-sticky">${arg.event.title}</div></div>`;

    //if forecast date is not empty, add checkmark icon
    if (
      arg.event.extendedProps.hasOwnProperty('forecast') &&
      arg.event.extendedProps.forecast !== ''
    ) {
      customhtml += `<i class="material-icons calendar-checkmark">check_circle_icon</i>`;
    }

    //if target date is not empty, add warning icon
    if (
      arg.event.extendedProps.hasOwnProperty('targetdate') &&
      arg.event.extendedProps.targetdate !== undefined
    ) {
      if (arg.event.extendedProps.targetIsGTE14days) {
        customhtml += `<i class="material-icons target-danger">warning</i>`;
      } else {
        customhtml += `<i class="material-icons target-warning">warning</i>`;
      }
    }

    customhtml += `</div>`;
    customhtml += `<div class="details"><div class="inner"><div class="content"><hr\>${arg.event.extendedProps.description}</div></div></div>`;
    return { html: customhtml };
  }

  eventApiToStore(eventApi: any): EventStore {
    let instance = eventApi._instance;
    let def = eventApi._def;

    return {
      defs: { [def.defId]: def },
      instances: instance ? { [instance.instanceId]: instance } : {},
    };
  }

  mutateEvent(eventApi: any, mutation: EventMutation): boolean {
    let instance = eventApi._instance;
    if (instance) {
      let context = eventApi._context;
      let def = eventApi._def;
      let { eventStore } = context.getCurrentData(); // gets current store for reverting
      let miniStore = this.eventApiToStore(eventApi); //builds a mini EventStore for us to mutate
      let eventConfigBase = {
        '': {
          // HACK. always allow API to mutate events : I don't get this, it was in the code from fullcalendar
          display: '',
          startEditable: true,
          durationEditable: true,
          constraints: [],
          overlap: null,
          allows: [],
          backgroundColor: '',
          borderColor: '',
          textColor: '',
          classNames: [],
        },
      } as EventUiHash;
      // mutate our little guy
      let mutatedEvents = applyMutationToEventStore(
        miniStore,
        eventConfigBase,
        mutation,
        context
      );

      //store oldEvent for the hook
      let oldEvent = buildEventApis(context, eventApi._def, instance);
      eventApi._instance = mutatedEvents.instances[instance.instanceId];
      eventApi._def = mutatedEvents.defs[def.defId];

      context.dispatch({ type: 'MERGE_EVENTS', eventStore: mutatedEvents });

      //fire off event hook for changing event.
      context.emitter.trigger('eventChange', {
        oldEvent,
        event: eventApi,
        relatedEvents: [],
        revert() {
          context.dispatch({ type: 'RESET_EVENTS', eventStore });
        },
      });
      return true;
    }
    return false;
  }

  //Intercepts clicks on deal/fund events so they route through Angular instead of doing a full page reload
  eventClick(arg: any): void {
    if (arg.event.url) {
      arg.jsEvent.preventDefault();
      this.router.navigateByUrl(arg.event.url.replace(this.env.nova_url, ''));
    }
  }

  dayClick(arg: any) {
    if (this.blockDatesActivate && !this.blockedWaiting) {
      this.blockedWaiting = true;
      let variable = '';
      let removeDate = false;
      let today = new Date().toISOString().slice(0, 10);
      if (arg.dateStr === today) {
        if (arg.dayEl.classList.contains('blocked-out-today')) {
          arg.dayEl.classList.remove('blocked-out-today');
          removeDate = true;
        } else {
          arg.dayEl.classList.add('blocked-out-today');
          variable = today;
        }
      } else {
        if (arg.dayEl.classList.contains('blocked-out-days')) {
          arg.dayEl.classList.remove('blocked-out-days');
          removeDate = true;
        } else {
          arg.dayEl.classList.add('blocked-out-days');
          variable = arg.dateStr;
        }
      }

      if (removeDate) {
        for (
          let i = 0;
          i < this.blockedResults.blockedCommitteeDates.length;
          i++
        ) {
          if (
            this.blockedResults.blockedCommitteeDates[i].date === arg.dateStr
          ) {
            variable = this.blockedResults.blockedCommitteeDates[i].id;
            break;
          }
        }

        this.apollo
          .mutate({
            mutation: DELETE_BLOCKED_COMMITTEE_DATE,
            variables: {
              id: variable,
            },
          })
          .subscribe(
            ({ data }) => {
              this.blockedWaiting = false;
              for (
                let i = 0;
                i < this.blockedResults.blockedCommitteeDates.length;
                i++
              ) {
                if (
                  this.blockedResults.blockedCommitteeDates[i].id === variable
                ) {
                  //copy both the parent object and the array to splice the date from the array
                  let copy: any = { ...this.blockedResults };
                  let arrCopy: any = [...copy.blockedCommitteeDates];
                  arrCopy.splice(i, 1);
                  copy.blockedCommitteeDates = arrCopy;
                  this.blockedResults = copy;
                  break;
                }
              }
            },
            (error) => {
              this.blockedWaiting = false;
              //if an error comes back from the API, undo the class changes
              if (arg.dateStr === today) {
                if (arg.dayEl.classList.contains('blocked-out-today')) {
                  arg.dayEl.classList.remove('blocked-out-today');
                } else {
                  arg.dayEl.classList.add('blocked-out-today');
                }
              } else {
                if (arg.dayEl.classList.contains('blocked-out-days')) {
                  arg.dayEl.classList.remove('blocked-out-days');
                } else {
                  arg.dayEl.classList.add('blocked-out-days');
                }
              }
            }
          );
      } else {
        this.apollo
          .mutate({
            mutation: ADD_BLOCKED_COMMITTEE_DATE,
            variables: {
              date: variable,
            },
          })
          .subscribe(
            ({ data }) => {
              this.blockedWaiting = false;
              let results: any = <object>data;
              let copy: any = { ...this.blockedResults };
              let arrCopy: any = [...copy.blockedCommitteeDates];
              arrCopy.push(results.addBlockedCommitteeDate);
              copy.blockedCommitteeDates = arrCopy;
              this.blockedResults = copy;
            },
            (error) => {
              this.blockedWaiting = false;
              //if an error comes back from the API, undo the class changes
              if (arg.dateStr === today) {
                if (arg.dayEl.classList.contains('blocked-out-today')) {
                  arg.dayEl.classList.remove('blocked-out-today');
                } else {
                  arg.dayEl.classList.add('blocked-out-today');
                }
              } else {
                if (arg.dayEl.classList.contains('blocked-out-days')) {
                  arg.dayEl.classList.remove('blocked-out-days');
                } else {
                  arg.dayEl.classList.add('blocked-out-days');
                }
              }
            }
          );
      }
    } else if (!this.blockDatesActivate) {
      this.router.navigate(['/dashboards/' + arg.dateStr]);
    }
  }

  toggleBlockDatesActivate() {
    this.blockDatesActivate = !this.blockDatesActivate;
  }

  //----------Old toggleGoup function---------------
  // toggleGroup(key: string): boolean {
  //   let events = this.calendarComponent.getApi().getEvents();
  //   let display = this.eventManagerService.toggleEventDisplay(key);
  //   for (let i = 0; i < events.length; i++) {
  //     let event = events[i];
  //     let group = this.eventManagerService.eventTypes[key];
  //     if (event.groupId === group.groupId) {
  //       event.setProp('display', display);
  //       return false;
  //     }
  //   }
  //   return false;
  // }

  toggleGroup(key:string): boolean {
    this.eventManagerService.toggleEventDisplay(key)
    this._refreshEventDisplay();
    return false;
  }

  toggleUserDeals(){
    this.eventManagerService.toggleUserDeals()
    this._refreshEventDisplay();
  }

  private _refreshEventDisplay(): void {
    this._events = this._events.map((event) => ({
      ...event,
      display: this.eventManagerService.computeDisplay(
        event.groupName,
        !!event.extendedProps?.isMine
      ),
    }));
    this.events.next(this._events);
  }

  ngOnDestroy() {
    this.querySubscription.unsubscribe();
    this.readyDateSubscription.unsubscribe();
    this.blockedDatesSubscription.unsubscribe();
    this.dateChangeSub.unsubscribe();
    this.dateSub.unsubscribe();
  }
}
